import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Switch,
  Chip,
  FormControl,
  Alert,
  InputAdornment,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import DropDown from "../../Subcomponents/DropDown";
import {
  getActiveProjects,
  getCPList,
  getBlockedAopCPs,
} from "../../Services/Brokerage";

const steps = [
  { label: "Validity Period", sub: "Financial year" },
  { label: "Calculation Rules", sub: "Define slabs" },
  { label: "Eligibility", sub: "Optional constraints" },
  { label: "CP Sign-Off", sub: "Configure window" },
  { label: "Exclusions", sub: "POP projects" },
  { label: "Review", sub: "Preview & save" },
];

const getFYDateRange = (fy) => {
  if (!fy) return {};

  const parts = fy.replace("FY ", "").split("-");
  const startYear = parseInt(parts[0]);
  const endYear = parseInt(parts[1]);

  return {
    start: `${startYear}-04-01`,
    end: `${endYear}-03-31`,
  };
};

const generateFinancialYears = (count = 6) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentFYStart = currentMonth >= 3 ? currentYear : currentYear - 1;
  const startYear = currentFYStart - 1; // include last (previous) FY

  return Array.from({ length: count }, (_, i) => {
    const from = startYear + i;
    const to = from + 1;
    return `FY ${from}-${to}`;
  });
};

export default function AOPForm({
  data: formData,
  setData: setFormData,
  onBack,
  onNext,
}) {
  const financialYears = generateFinancialYears(6);

  const [cpList, setCpList] = React.useState([]);
  const [blockedCPs, setBlockedCPs] = React.useState(new Set());
  const [blockAllCPs, setBlockAllCPs] = React.useState(false);
  const [projects, setProjects] = React.useState([]);

  const [projectSearch, setProjectSearch] = React.useState("");
  const [cpSearch, setCpSearch] = React.useState("");

  const isFYSelected = !!formData.financialYear;

  const canProceedScopes =
    !!formData.financialYear &&
    !!formData.validityStartDate &&
    !!formData.validityEndDate;

  const boxStyle = {
    border: "1px solid #E5E7EB",
    borderRadius: 2,
    p: 3,
    mb: 3,
    backgroundColor: "#fff",
  };

  const handleNext = () => {
    onNext();
  };

  useEffect(() => {
    const loadBlockedCPs = async () => {
      if (!formData.validityStartDate || !formData.validityEndDate) {
        setBlockedCPs(new Set());
        setBlockAllCPs(false);
        return;
      }

      const result = await getBlockedAopCPs(
        formData.validityStartDate,
        formData.validityEndDate
      );

      setBlockedCPs(result.blockedCPs || new Set());
      setBlockAllCPs(result.blockAll || false);
    };

    loadBlockedCPs();
  }, [formData.validityStartDate, formData.validityEndDate]);

  useEffect(() => {
    if (formData.cpScopeType !== "SPECIFIC") return;

    if (blockAllCPs) {
      setFormData((prev) => ({
        ...prev,
        selectedCPs: [],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedCPs: (prev.selectedCPs || []).filter(
        (cpId) => !blockedCPs.has(cpId)
      ),
    }));
  }, [blockedCPs, blockAllCPs]);

  useEffect(() => {
    const loadData = async () => {
      const [projectsData, cpData] = await Promise.all([
        getActiveProjects(),
        getCPList(),
      ]);

      setProjects(projectsData || []);
      setCpList(cpData || []);
    };

    loadData();
  }, []);

  const toggleExcludedProject = (projectId) => {
    setFormData((prev) => {
      const current = prev.excludedProjects || [];

      return {
        ...prev,
        excludedProjects: current.includes(projectId)
          ? current.filter((id) => id !== projectId)
          : [...current, projectId],
      };
    });
  };

  const toggleSelectedCP = (cpId) => {
    setFormData((prev) => {
      const current = prev.selectedCPs || [];

      return {
        ...prev,
        selectedCPs: current.includes(cpId)
          ? current.filter((id) => id !== cpId)
          : [...current, cpId],
      };
    });
  };

  const filteredProjects = (projects || []).filter((p) =>
    (p.projectName || "").toLowerCase().includes(projectSearch.toLowerCase())
  );

  const filteredCPs = (cpList || []).filter((cp) =>
    (cp.billingName || "").toLowerCase().includes(cpSearch.toLowerCase())
  );

  const selectableFilteredCPs = filteredCPs.filter(
    (cp) =>
      !blockAllCPs &&
      !blockedCPs.has(cp.cpId) &&
      formData.validityStartDate &&
      formData.validityEndDate
  );

  const allFilteredCPsSelected =
    selectableFilteredCPs.length > 0 &&
    selectableFilteredCPs.every((cp) =>
      formData.selectedCPs?.includes(cp.cpId)
    );

  const someFilteredCPsSelected = selectableFilteredCPs.some((cp) =>
    formData.selectedCPs?.includes(cp.cpId)
  );

  const toggleSelectAllCPs = () => {
    const selectableIds = selectableFilteredCPs.map((cp) => cp.cpId);

    setFormData((prev) => {
      const current = prev.selectedCPs || [];

      return {
        ...prev,
        selectedCPs: allFilteredCPsSelected
          ? current.filter((id) => !selectableIds.includes(id))
          : Array.from(new Set([...current, ...selectableIds])),
      };
    });
  };

  return (
    <Box sx={{ p: 1, minHeight: "100vh" }}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{
          mb: 3,
          cursor: "pointer",
          color: "text.secondary",
          fontSize: 14,
          fontWeight: 500,
        }}
        onClick={onBack}
      >
        ← Back to AOPs
      </Typography>

      {/* Stepper */}
      <Stepper
        activeStep={0}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepLabel-label": {
            fontSize: "0.95rem",
            fontWeight: 500,
          },
          "& .MuiStepIcon-root": {
            fontSize: 32,
          },
          "& .Mui-active": {
            color: "#2563EB",
          },
          "& .Mui-completed": {
            color: "#2563EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Box textAlign="center">
                <Typography fontSize="0.95rem" fontWeight={600}>
                  {step.label}
                </Typography>
                <Typography fontSize="0.75rem" color="text.secondary">
                  {step.sub}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Info banner */}
      <Alert
        icon={<InfoOutlinedIcon />}
        severity="info"
        sx={{
          mb: 3,
          bgcolor: "#eef6ff",
          color: "#1e3a8a",
          border: "1px solid #c7ddff",
        }}
      >
        <Typography fontSize={13}>
          <b>AOP Achievement Logic</b>
          <br />
          AOP achievement is calculated only on successful agreements.
        </Typography>
      </Alert>

      {/* Financial Year & Status Box */}
      <Box sx={boxStyle}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CalendarMonthIcon color="primary" />
          <Typography fontWeight={600}>Financial Year & Status</Typography>
        </Box>

        <FormControl size="small" sx={{ width: 220, mb: 3 }}>
          <DropDown
            label="Financial Year"
            list={financialYears.map((fy) => ({
              label: fy,
              value: fy,
            }))}
            selectedValue={formData.financialYear || ""}
            placeHolder="Select Financial Year"
            onChangeSelect={(value) => {
              const range = getFYDateRange(value);

              setFormData((prev) => ({
                ...prev,
                financialYear: value,
                validityStartDate: range.start || "",
                validityEndDate: range.end || "",
                excludedProjects: [],
                selectedCPs: [],
                scopeType: "ALL",
                cpScopeType: "ALL",
              }));
            }}
            mandatory
          />
        </FormControl>

        {!formData.financialYear && (
          <Alert severity="warning" sx={{ mb: 2, maxWidth: 500 }}>
            Please select Financial Year first.
          </Alert>
        )}

        <Box mt={3}>
          <Typography fontWeight={600} mb={2}>
            Validity Period
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              maxWidth: 500,
              mb: 2,
            }}
          >
            {/* START DATE */}
            <Box>
              <Typography fontWeight={500} mb={1}>
                Start Date
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.validityStartDate || ""}
                disabled={!isFYSelected}
                onChange={(e) => {
                  const newDate = e.target.value;

                  setFormData((prev) => ({
                    ...prev,
                    validityStartDate: newDate,
                    validityEndDate: "",
                    selectedCPs: [],
                  }));
                }}
              />
            </Box>

            {/* END DATE */}
            <Box>
              <Typography fontWeight={500} mb={1}>
                End Date
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.validityEndDate || ""}
                disabled={!isFYSelected || !formData.validityStartDate}
                onChange={(e) => {
                  const newDate = e.target.value;

                  setFormData((prev) => ({
                    ...prev,
                    validityEndDate: newDate,
                    selectedCPs: [],
                  }));
                }}
              />
            </Box>
          </Box>
        </Box>

        <Typography fontSize={12} color="text.secondary" mt={1}>
          Only one active AOP per financial year is allowed.
        </Typography>
      </Box>

      {/* PROJECT SCOPE */}
      <Box
        sx={{
          ...boxStyle,
          opacity: !canProceedScopes ? 0.6 : 1,
          pointerEvents: !canProceedScopes ? "none" : "auto",
        }}
      >
        <Typography fontWeight={600} mb={2}>
          Project Scope
        </Typography>

        {!canProceedScopes && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please select Financial Year and Validity Period first.
          </Alert>
        )}

        <RadioGroup
          row
          value={formData.scopeType || "ALL"}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              scopeType: e.target.value,
              excludedProjects:
                e.target.value === "ALL" ? [] : prev.excludedProjects,
            }))
          }
        >
          <FormControlLabel
            value="ALL"
            control={<Radio />}
            label="All Projects"
          />
          <FormControlLabel
            value="EXCLUDE"
            control={<Radio />}
            label="Exclude Projects"
          />
        </RadioGroup>

        {formData.scopeType === "EXCLUDE" && (
          <Box mt={2}>
            {/* Search Right */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <TextField
                size="small"
                placeholder="Search project..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                sx={{ width: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                {filteredProjects.map((p) => {
                  const selected = formData.excludedProjects?.includes(
                    p.projectId
                  );

                  return (
                    <Box
                      key={p.projectId}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: selected ? "#2563EB" : "#E5E7EB",
                        background: selected ? "#EFF6FF" : "#FFFFFF",
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onClick={() => toggleExcludedProject(p.projectId)}
                    >
                      <Typography fontWeight={600}>{p.projectName}</Typography>

                      {selected && (
                        <Typography
                          fontSize={12}
                          sx={{ color: "#2563EB", mt: 0.5 }}
                        >
                          Selected
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {filteredProjects.length === 0 && (
                <Typography fontSize={13} color="text.secondary" mt={2}>
                  No projects found.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* CP SCOPE */}
      <Box
        sx={{
          ...boxStyle,
          opacity: !canProceedScopes ? 0.6 : 1,
          pointerEvents: !canProceedScopes ? "none" : "auto",
        }}
      >
        <Typography fontWeight={600} mb={2}>
          CP Scope
        </Typography>

        {!canProceedScopes && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please select Financial Year and Validity Period first.
          </Alert>
        )}

        <RadioGroup
          row
          value={formData.cpScopeType || "ALL"}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              cpScopeType: e.target.value,
              selectedCPs:
                e.target.value === "ALL" ? [] : prev.selectedCPs || [],
            }))
          }
        >
          <FormControlLabel value="ALL" control={<Radio />} label="All CPs" />
          <FormControlLabel
            value="SPECIFIC"
            control={<Radio />}
            label="Specific CPs"
          />
        </RadioGroup>

        {formData.cpScopeType === "SPECIFIC" && (
          <Box mt={2}>
            {(!formData.validityStartDate || !formData.validityEndDate) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select validity dates first to see available CPs.
              </Alert>
            )}

            {blockAllCPs &&
              formData.validityStartDate &&
              formData.validityEndDate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  All CPs are already covered in another AOP for this date range.
                </Alert>
              )}

            {/* Select All + Search */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allFilteredCPsSelected}
                    indeterminate={
                      !allFilteredCPsSelected && someFilteredCPsSelected
                    }
                    onChange={toggleSelectAllCPs}
                    disabled={selectableFilteredCPs.length === 0}
                  />
                }
                label="Select All"
              />
              <TextField
                size="small"
                placeholder="Search CP..."
                value={cpSearch}
                onChange={(e) => setCpSearch(e.target.value)}
                sx={{ width: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                {filteredCPs.map((cp) => {
                  const selected = formData.selectedCPs?.includes(cp.cpId);

                  const isBlocked =
                    blockAllCPs ||
                    blockedCPs.has(cp.cpId) ||
                    !formData.validityStartDate ||
                    !formData.validityEndDate;

                  return (
                    <Box
                      key={cp.cpId}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: selected ? "#2563EB" : "#E5E7EB",
                        background: selected
                          ? "#EFF6FF"
                          : isBlocked
                          ? "#F9FAFB"
                          : "#FFFFFF",
                        borderRadius: 2,
                        cursor: isBlocked ? "not-allowed" : "pointer",
                        transition: "all 0.2s",
                        opacity: isBlocked ? 0.6 : 1,
                      }}
                      onClick={() => {
                        if (isBlocked) return;
                        toggleSelectedCP(cp.cpId);
                      }}
                    >
                      <Typography fontWeight={600}>{cp.billingName}</Typography>

                      {selected && (
                        <Typography
                          fontSize={12}
                          sx={{ color: "#2563EB", mt: 0.5 }}
                        >
                          Selected
                        </Typography>
                      )}

                      {!selected && blockedCPs.has(cp.cpId) && !blockAllCPs && (
                        <Typography
                          fontSize={12}
                          sx={{ color: "#DC2626", mt: 0.5 }}
                        >
                          Already used in another AOP
                        </Typography>
                      )}

                      {!selected && blockAllCPs && (
                        <Typography
                          fontSize={12}
                          sx={{ color: "#DC2626", mt: 0.5 }}
                        >
                          Covered by All CP AOP
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {filteredCPs.length === 0 && (
                <Typography fontSize={13} color="text.secondary" mt={2}>
                  No CP found.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* STATUS */}
      <Box
        sx={{
          ...boxStyle,
          opacity: !canProceedScopes ? 0.7 : 1,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography fontSize={14} fontWeight={500}>
              Status
            </Typography>
            <Typography fontSize={12} color="text.secondary">
              Activate this AOP for the selected year
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={formData.isActive ? "Active" : "Inactive"}
              size="small"
              sx={{
                bgcolor: formData.isActive ? "#dcfce7" : "#fee2e2",
                color: formData.isActive ? "#166534" : "#991b1b",
                fontWeight: 500,
              }}
            />
            <Switch
              checked={formData.isActive}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
              }
              color="primary"
              disabled={!canProceedScopes}
            />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={handleNext}
          disabled={
            !formData.financialYear ||
            !formData.validityStartDate ||
            !formData.validityEndDate ||
            (formData.cpScopeType === "SPECIFIC" &&
              (!formData.selectedCPs || formData.selectedCPs.length === 0))
          }
          sx={{ textTransform: "none" }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}